import { useState, type ReactNode, type CSSProperties } from 'react'

export interface Tab {
  label: string
  icon?: ReactNode
  content: ReactNode
}

interface PageTabsProps {
  tabs: Tab[]
  defaultTab?: number
  variant?: 'default' | 'broadcast'
  /** Controlled mode: when provided, the active tab is owned by the parent */
  activeTab?: number
  onTabChange?: (index: number) => void
}

// Broadcast is the app-wide default since the global re-skin; 'default'
// remains available for contexts where the angled tabs don't fit.
export function PageTabs({
  tabs,
  defaultTab = 0,
  variant = 'broadcast',
  activeTab,
  onTabChange,
}: PageTabsProps) {
  const [internal, setInternal] = useState(defaultTab)
  const active = activeTab ?? internal
  const setActive = (i: number) => {
    onTabChange?.(i)
    if (activeTab === undefined) setInternal(i)
  }

  if (tabs.length === 1) {
    return <>{tabs[0].content}</>
  }

  if (variant === 'broadcast') {
    // Desktop keeps the angled scroll strip; on mobile the strip becomes a clean
    // segmented control (see broadcast.css). --tab-cols drives the mobile grid so
    // 4 tabs land as a 2×2 and 2–3 tabs stay on a single row.
    const cols = tabs.length >= 4 ? 2 : tabs.length
    return (
      <div>
        <div
          className="glass-card-static page-tabs-strip"
          style={{ '--tab-cols': cols } as CSSProperties}
        >
          {tabs.map((tab, i) => (
            <button
              key={i}
              onClick={() => setActive(i)}
              className={`btn-angled page-tab${active === i ? ' active' : ''}`}
              style={{
                flexShrink: 0,
                whiteSpace: 'nowrap',
                background:
                  active === i ? 'var(--gradient-gold)' : 'var(--fill-soft)',
                color: active === i ? 'var(--gold-contrast)' : 'var(--text-secondary)',
                textShadow: active === i ? '0 1px 0 rgba(255, 255, 255, 0.25)' : 'none',
              }}
            >
              {tab.icon}
              {tab.label}
            </button>
          ))}
        </div>
        {tabs[active].content}
      </div>
    )
  }

  return (
    <div>
      {/* Tab bar */}
      <div
        className="glass-card-static"
        style={{
          display: 'flex',
          gap: '2px',
          marginBottom: '1.25rem',
          padding: '0.25rem',
          overflowX: 'auto',
        }}
      >
        {tabs.map((tab, i) => (
          <button
            key={i}
            onClick={() => setActive(i)}
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: '0.375rem',
              padding: '0.5rem 0.85rem',
              border: 'none',
              borderRadius: 'calc(var(--glass-radius) - 4px)',
              background: active === i ? 'var(--gold-fill)' : 'transparent',
              color: active === i ? 'var(--gold)' : 'var(--text-secondary)',
              fontWeight: active === i ? 600 : 400,
              fontSize: '0.875rem',
              cursor: 'pointer',
              whiteSpace: 'nowrap',
              transition: 'all 0.15s ease',
              ...(active === i ? { boxShadow: 'inset 0 -2px 0 var(--gold)' } : {}),
            }}
          >
            {tab.icon}
            {tab.label}
          </button>
        ))}
      </div>

      {/* Active content */}
      {tabs[active].content}
    </div>
  )
}
